import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Badge, Button, Dialog, Field, Input, Section, Select, Spinner, Textarea, type Tone } from '@zt/ui';
import type { ApiError } from '../lib/api';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  Category,
  GadsCampaign,
  GadsClaimFinding,
  GadsKeyword,
  GadsSearchAd,
  GadsSearchSection,
  GadsSettings,
  ProductPage,
} from '../lib/types';

/**
 * Arama kampanyaları (F7-40, ADR-148): duyarlı arama reklamı düzenleyicisi (başlık ≤ 30, açıklama ≤ 90, görünen yol
 * ≤ 15; sayaçlar canlı), ürün/kategori verisinden taslak (sunucu üretir, uydurma yok), reklam önizlemesi, anahtar
 * kelime ve negatif anahtar kelime düzenleyicisi, anahtar kelime önerisi (Google'da Basic erişim gerekir), kampanya
 * sayfasındaki arama bölümü (Google'daki durum, uzman incelemesi, metin ve liste değişikliği istekleri).
 */
const G = 'admin.gads.search';

export const LIMITS = { headline: 30, description: 90, path: 15, keyword: 80 } as const;
export const MATCH_TYPES = ['broad', 'phrase', 'exact'] as const;
export type MatchType = (typeof MATCH_TYPES)[number];
export type SearchAdValue = GadsSearchAd;
export type KeywordValue = GadsKeyword;

export const emptyAd = (url = ''): SearchAdValue => ({
  finalUrl: url,
  path1: null,
  path2: null,
  headlines: [
    { text: '', pin: null },
    { text: '', pin: null },
    { text: '', pin: null },
  ],
  descriptions: [
    { text: '', pin: null },
    { text: '', pin: null },
  ],
});

export const len = (s: string) => [...s].length;
const keyOf = (k: KeywordValue) => `${k.matchType}:${k.text.trim().toLocaleLowerCase('tr')}`;

/** İstemcide hızlı denetim (sunucu yine doğrular): sayılar ve sınırlar. */
export function adProblems(ad: SearchAdValue): string[] {
  const out: string[] = [];
  const hs = ad.headlines.filter((h) => h.text.trim());
  const ds = ad.descriptions.filter((d) => d.text.trim());
  if (hs.length < 3) out.push('headlines_min');
  if (ds.length < 2) out.push('descriptions_min');
  if (hs.some((h) => len(h.text.trim()) > LIMITS.headline)) out.push('headline_too_long');
  if (ds.some((d) => len(d.text.trim()) > LIMITS.description)) out.push('description_too_long');
  if (!/^https:\/\//.test(ad.finalUrl.trim())) out.push('final_url');
  return out;
}

/** Sunucuya giden biçim: boş satırlar atılır, metinler kırpılır. */
export function cleanAd(ad: SearchAdValue): SearchAdValue {
  return {
    finalUrl: ad.finalUrl.trim(),
    path1: ad.path1?.trim() || null,
    path2: ad.path2?.trim() || null,
    headlines: ad.headlines.filter((h) => h.text.trim()).map((h) => ({ ...h, text: h.text.trim() })),
    descriptions: ad.descriptions.filter((d) => d.text.trim()).map((d) => ({ ...d, text: d.text.trim() })),
  };
}

const padAd = (ad: SearchAdValue): SearchAdValue => {
  const pad = <T,>(xs: T[], n: number, blank: () => T) => [
    ...xs,
    ...Array.from({ length: Math.max(0, n - xs.length) }, blank),
  ];
  return {
    ...ad,
    headlines: pad(ad.headlines, 3, () => ({ text: '', pin: null })),
    descriptions: pad(ad.descriptions, 2, () => ({ text: '', pin: null })),
  };
};

function Counter({ value, max }: { value: string; max: number }) {
  const n = len(value);
  return (
    <span className={`text-xs tabular-nums ${n > max ? 'font-medium text-rose' : 'text-ink-3'}`} aria-hidden>
      {n}/{max}
    </span>
  );
}

interface Suggestion {
  ad: SearchAdValue;
  keywords: KeywordValue[];
  shortOf: { headlines: number; descriptions: number };
  findings: GadsClaimFinding[];
}

/** Ürün veya kategori seçip veriden taslak ister. */
function SourcePicker({ storeId, onSuggest }: { storeId: string; onSuggest: (r: Suggestion) => void }) {
  const t = useT();
  const [kind, setKind] = useState<'product' | 'category'>('category');
  const [id, setId] = useState('');
  const [q, setQ] = useState('');
  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/v1/admin/categories'),
    enabled: kind === 'category',
  });
  const products = useQuery({
    queryKey: ['gads-search-products', q],
    queryFn: () =>
      api.get<ProductPage>(
        `/v1/admin/products?status=active&limit=20${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}`,
      ),
    enabled: kind === 'product',
  });
  const suggest = useApiMutation(
    () => api.post<Suggestion>('/v1/admin/google-ads/search-suggestion', { storeId, source: { kind, id } }),
    { onSuccess: (r) => onSuggest(r) },
  );
  return (
    <fieldset className="space-y-3 rounded-md border border-line p-3">
      <legend className="flex items-center gap-1 px-1 text-sm font-medium">
        {t(`${G}.source`)} <HelpTip k="gads.search_source" />
      </legend>
      <div className="flex flex-wrap gap-4 text-sm">
        {(['category', 'product'] as const).map((k) => (
          <label key={k} className="inline-flex items-center gap-1.5">
            <input
              type="radio"
              name="gads-search-source"
              className="accent-[var(--zt-pine)]"
              checked={kind === k}
              onChange={() => {
                setKind(k);
                setId('');
              }}
            />
            {t(`${G}.sourceKinds.${k}`)}
          </label>
        ))}
      </div>
      {kind === 'category' ? (
        <Field label={t(`${G}.sourceCategory`)}>
          {(p) => (
            <Select {...p} value={id} onChange={(e) => setId(e.target.value)}>
              <option value="">—</option>
              {(cats.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label={t(`${G}.sourceSearch`)}>
            {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
          </Field>
          <Field label={t(`${G}.sourceProduct`)}>
            {(p) => (
              <Select {...p} value={id} onChange={(e) => setId(e.target.value)}>
                <option value="">—</option>
                {(products.data?.items ?? []).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.title}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button disabled={!id} loading={suggest.isPending} onClick={() => suggest.mutate(undefined)}>
          {t(`${G}.suggest`)}
        </Button>
        <HelpTip k="gads.search_suggest" />
      </div>
    </fieldset>
  );
}

/** Google arama sonucuna benzer önizleme: ilk üç başlık ve ilk iki açıklama (Google sırayı kendisi seçer). */
export function SearchAdPreview({ ad }: { ad: SearchAdValue }) {
  const t = useT();
  const host = URL.canParse(ad.finalUrl) ? new URL(ad.finalUrl).host : '';
  const hs = ad.headlines.filter((h) => h.text.trim()).slice(0, 3);
  const ds = ad.descriptions.filter((d) => d.text.trim()).slice(0, 2);
  return (
    <figure className="space-y-1 rounded-md border border-line bg-surface p-3" aria-label={t(`${G}.preview`)}>
      <figcaption className="text-xs text-ink-3">{t(`${G}.previewCaption`)}</figcaption>
      <p className="text-xs text-ink-2">
        <span className="font-semibold text-ink">{t(`${G}.sponsored`)}</span>{' '}
        <span dir="ltr">{[host, ad.path1, ad.path2].filter(Boolean).join('/')}</span>
      </p>
      <p className="text-lg leading-snug text-pine">
        {hs.map((h) => h.text.trim()).join(' | ') || t(`${G}.previewEmpty`)}
      </p>
      <p className="text-sm text-ink-2">{ds.map((d) => d.text.trim()).join(' ')}</p>
    </figure>
  );
}

function Findings({ findings }: { findings: GadsClaimFinding[] }) {
  const t = useT();
  if (!findings.length) return null;
  return (
    <div className="rounded-md border border-amber/30 bg-amber-tint p-3 text-sm" role="status">
      <p className="font-medium">{t(`${G}.findings`, { count: findings.length })}</p>
      <ul className="mt-1 list-disc space-y-0.5 ps-5">
        {findings.map((f, i) => (
          <li key={i}>
            <strong>{f.match}</strong> — {t(`healthClaims.${f.code}`)}: “{f.excerpt}”
          </li>
        ))}
      </ul>
      <p className="mt-1 text-ink-2">{t(`${G}.findingsLead`)}</p>
    </div>
  );
}

export function SearchAdEditor({
  value,
  onChange,
  storeId,
  findings,
  errors,
}: {
  value: SearchAdValue;
  onChange: (v: SearchAdValue, keywords?: KeywordValue[]) => void;
  storeId: string;
  findings: GadsClaimFinding[];
  errors: ApiError | null;
}) {
  const t = useT();
  const [short, setShort] = useState<{ headlines: number; descriptions: number } | null>(null);
  const [suggested, setSuggested] = useState<GadsClaimFinding[]>([]);
  const setHeadline = (i: number, patch: Partial<SearchAdValue['headlines'][number]>) =>
    onChange({ ...value, headlines: value.headlines.map((h, j) => (j === i ? { ...h, ...patch } : h)) });
  const setDescription = (i: number, patch: Partial<SearchAdValue['descriptions'][number]>) =>
    onChange({
      ...value,
      descriptions: value.descriptions.map((d, j) => (j === i ? { ...d, ...patch } : d)),
    });
  const fieldErr = (path: string) => errors?.errors.find((e) => e.field.endsWith(path))?.message;
  return (
    <div className="space-y-4">
      <SourcePicker
        storeId={storeId}
        onSuggest={(r) => {
          onChange(padAd(r.ad), r.keywords);
          setShort(r.shortOf);
          setSuggested(r.findings);
        }}
      />
      {short && (short.headlines > 0 || short.descriptions > 0) && (
        <p className="text-sm text-amber" role="status">
          {t(`${G}.shortOf`, { headlines: short.headlines, descriptions: short.descriptions })}
        </p>
      )}
      <Field
        label={t(`${G}.finalUrl`)}
        required
        description={t(`${G}.finalUrlHint`)}
        help={<HelpTip k="gads.search_final_url" />}
        error={fieldErr('finalUrl')}
      >
        {(p) => (
          <Input
            {...p}
            type="url"
            dir="ltr"
            value={value.finalUrl}
            onChange={(e) => onChange({ ...value, finalUrl: e.target.value })}
          />
        )}
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        {(['path1', 'path2'] as const).map((k) => (
          <Field
            key={k}
            label={t(`${G}.${k}`)}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="gads.search_path" />}
            error={fieldErr(k)}
          >
            {(p) => (
              <div className="flex items-center gap-2">
                <Input
                  {...p}
                  value={value[k] ?? ''}
                  onChange={(e) => onChange({ ...value, [k]: e.target.value.replace(/\s+/g, '-') })}
                />
                <Counter value={value[k] ?? ''} max={LIMITS.path} />
              </div>
            )}
          </Field>
        ))}
      </div>
      <fieldset className="space-y-2">
        <legend className="flex items-center gap-1 text-sm font-medium">
          {t(`${G}.headlines`)} <HelpTip k="gads.search_headlines" />
        </legend>
        <p className="text-[0.8125rem] text-ink-3">{t(`${G}.headlinesHint`)}</p>
        {value.headlines.map((h, i) => (
          <div key={i} className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                aria-label={t(`${G}.headlineN`, { n: i + 1 })}
                className="min-w-0 flex-1"
                value={h.text}
                onChange={(e) => setHeadline(i, { text: e.target.value })}
                aria-invalid={
                  len(h.text.trim()) > LIMITS.headline || !!fieldErr(`headlines.${i}.text`) || undefined
                }
              />
              <Counter value={h.text.trim()} max={LIMITS.headline} />
              <Select
                aria-label={t(`${G}.pinN`, { n: i + 1 })}
                className="w-36"
                value={h.pin ?? ''}
                onChange={(e) =>
                  setHeadline(i, {
                    pin: (e.target.value || null) as SearchAdValue['headlines'][number]['pin'],
                  })
                }
              >
                <option value="">{t(`${G}.pinNone`)}</option>
                {(['h1', 'h2', 'h3'] as const).map((p) => (
                  <option key={p} value={p}>
                    {t(`${G}.pins.${p}`)}
                  </option>
                ))}
              </Select>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t(`${G}.removeHeadline`, { n: i + 1 })}
                disabled={value.headlines.length <= 3}
                onClick={() => onChange({ ...value, headlines: value.headlines.filter((_, j) => j !== i) })}
              >
                <Trash2 size={16} aria-hidden />
              </Button>
            </div>
            {fieldErr(`headlines.${i}.text`) && (
              <p className="text-xs text-rose">{fieldErr(`headlines.${i}.text`)}</p>
            )}
          </div>
        ))}
        {fieldErr('headlines') && <p className="text-xs text-rose">{fieldErr('headlines')}</p>}
        <Button
          size="sm"
          disabled={value.headlines.length >= 15}
          onClick={() => onChange({ ...value, headlines: [...value.headlines, { text: '', pin: null }] })}
        >
          <Plus size={16} aria-hidden /> {t(`${G}.addHeadline`)}
        </Button>
      </fieldset>
      <fieldset className="space-y-2">
        <legend className="flex items-center gap-1 text-sm font-medium">
          {t(`${G}.descriptions`)} <HelpTip k="gads.search_descriptions" />
        </legend>
        {value.descriptions.map((d, i) => (
          <div key={i} className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                aria-label={t(`${G}.descriptionN`, { n: i + 1 })}
                className="min-w-0 flex-1"
                value={d.text}
                onChange={(e) => setDescription(i, { text: e.target.value })}
                aria-invalid={
                  len(d.text.trim()) > LIMITS.description || !!fieldErr(`descriptions.${i}.text`) || undefined
                }
              />
              <Counter value={d.text.trim()} max={LIMITS.description} />
              <Select
                aria-label={t(`${G}.pinDescN`, { n: i + 1 })}
                className="w-36"
                value={d.pin ?? ''}
                onChange={(e) =>
                  setDescription(i, {
                    pin: (e.target.value || null) as SearchAdValue['descriptions'][number]['pin'],
                  })
                }
              >
                <option value="">{t(`${G}.pinNone`)}</option>
                {(['d1', 'd2'] as const).map((p) => (
                  <option key={p} value={p}>
                    {t(`${G}.pins.${p}`)}
                  </option>
                ))}
              </Select>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t(`${G}.removeDescription`, { n: i + 1 })}
                disabled={value.descriptions.length <= 2}
                onClick={() =>
                  onChange({ ...value, descriptions: value.descriptions.filter((_, j) => j !== i) })
                }
              >
                <Trash2 size={16} aria-hidden />
              </Button>
            </div>
            {fieldErr(`descriptions.${i}.text`) && (
              <p className="text-xs text-rose">{fieldErr(`descriptions.${i}.text`)}</p>
            )}
          </div>
        ))}
        <Button
          size="sm"
          disabled={value.descriptions.length >= 4}
          onClick={() =>
            onChange({ ...value, descriptions: [...value.descriptions, { text: '', pin: null }] })
          }
        >
          <Plus size={16} aria-hidden /> {t(`${G}.addDescription`)}
        </Button>
      </fieldset>
      <SearchAdPreview ad={value} />
      <Findings findings={findings.length ? findings : suggested} />
    </div>
  );
}

export function KeywordsEditor({
  label,
  help,
  lead,
  value,
  onChange,
  max,
}: {
  label: string;
  help: 'gads.search_keywords' | 'gads.search_negatives';
  lead: string;
  value: KeywordValue[];
  onChange: (v: KeywordValue[]) => void;
  max: number;
}) {
  const t = useT();
  const [bulk, setBulk] = useState('');
  const add = (texts: string[]) => {
    const seen = new Set(value.map(keyOf));
    const next = [...value];
    for (const raw of texts) {
      const k: KeywordValue = { text: raw.replace(/\s+/g, ' ').trim(), matchType: 'phrase' };
      if (!k.text || seen.has(keyOf(k)) || next.length >= max) continue;
      seen.add(keyOf(k));
      next.push(k);
    }
    onChange(next);
  };
  return (
    <fieldset className="space-y-2">
      <legend className="flex items-center gap-1 text-sm font-medium">
        {label} <HelpTip k={help} />
      </legend>
      <p className="text-[0.8125rem] text-ink-3">{lead}</p>
      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((k, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2">
              <Input
                aria-label={t(`${G}.keywordN`, { label, n: i + 1 })}
                className="min-w-0 flex-1"
                value={k.text}
                maxLength={LIMITS.keyword}
                onChange={(e) =>
                  onChange(value.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))
                }
              />
              <Select
                aria-label={t(`${G}.matchTypeN`, { label, n: i + 1 })}
                className="w-40"
                value={k.matchType}
                onChange={(e) =>
                  onChange(
                    value.map((x, j) => (j === i ? { ...x, matchType: e.target.value as MatchType } : x)),
                  )
                }
              >
                {MATCH_TYPES.map((m) => (
                  <option key={m} value={m}>
                    {t(`${G}.matchTypes.${m}`)}
                  </option>
                ))}
              </Select>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t(`${G}.removeKeyword`, { text: k.text || String(i + 1) })}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              >
                <Trash2 size={16} aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <Field
          label={t(`${G}.addKeywords`, { label })}
          description={t(`${G}.addKeywordsHint`)}
          className="min-w-0 flex-1"
        >
          {(p) => <Textarea {...p} rows={2} value={bulk} onChange={(e) => setBulk(e.target.value)} />}
        </Field>
        <Button
          disabled={!bulk.trim() || value.length >= max}
          onClick={() => {
            add(bulk.split(/\n|;/));
            setBulk('');
          }}
        >
          <Plus size={16} aria-hidden /> {t(`${G}.add`)}
        </Button>
      </div>
      <p className="text-xs text-ink-3">{t(`${G}.keywordCount`, { count: value.length, max })}</p>
    </fieldset>
  );
}

/** Anahtar kelime önerisi: Google'da Basic erişim gerekir (Explorer'da planlama kapalı). */
export function KeywordIdeas({
  storeId,
  seeds,
  url,
  onAdd,
}: {
  storeId: string;
  seeds: string[];
  url: string;
  onAdd: (texts: string[]) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const settings = useQuery({
    queryKey: ['gads-settings'],
    queryFn: () => api.get<GadsSettings>('/v1/admin/google-ads/settings'),
  });
  const ideas = useApiMutation(
    () =>
      api.post<{
        ideas: Array<{ text: string; avgMonthlySearches: number | null; competition: string | null }>;
      }>('/v1/admin/google-ads/keyword-ideas', {
        storeId,
        seeds: seeds.slice(0, 10),
        url: /^https:\/\//.test(url.trim()) ? url.trim() : null,
      }),
    {},
  );
  const denied = ideas.error?.code === 'google_ads_basic_access_required';
  const locked = (settings.data?.accessLevel ?? 'explorer') === 'explorer' || denied;
  const num = new Intl.NumberFormat({ tr: 'tr-TR', en: 'en-GB', de: 'de-DE', ar: 'ar' }[locale] ?? 'tr-TR');
  return (
    <div className="space-y-2 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          disabled={locked || (!seeds.length && !/^https:\/\//.test(url.trim()))}
          loading={ideas.isPending}
          onClick={() => ideas.mutate(undefined)}
        >
          {t(`${G}.ideas`)}
        </Button>
        {locked && <Badge tone="amber">{t(`${G}.basicRequired`)}</Badge>}
        <HelpTip k="gads.search_ideas" />
      </div>
      {locked && <p className="text-[0.8125rem] text-ink-2">{t(`${G}.basicRequiredLead`)}</p>}
      {ideas.isPending && <Spinner label={t('common.loading')} />}
      {ideas.data && !ideas.data.ideas.length && <p className="text-sm text-ink-3">{t(`${G}.noIdeas`)}</p>}
      {ideas.data && ideas.data.ideas.length > 0 && (
        <ul className="divide-y divide-line text-sm" aria-label={t(`${G}.ideas`)}>
          {ideas.data.ideas.map((i) => (
            <li key={i.text} className="flex items-center justify-between gap-2 py-1">
              <span>
                {i.text}{' '}
                <span className="text-ink-3">
                  {i.avgMonthlySearches !== null
                    ? t(`${G}.monthlySearches`, { n: num.format(i.avgMonthlySearches) })
                    : ''}
                  {i.competition ? ` · ${t(`${G}.competition.${i.competition}`)}` : ''}
                </span>
              </span>
              <Button
                size="sm"
                aria-label={t(`${G}.addIdea`, { text: i.text })}
                onClick={() => onAdd([i.text])}
              >
                {t(`${G}.add`)}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Kampanya sayfasındaki arama bölümü

const approvalTone: Record<string, Tone> = {
  APPROVED: 'moss',
  APPROVED_LIMITED: 'amber',
  AREA_OF_INTEREST_ONLY: 'amber',
  DISAPPROVED: 'rose',
};
const reviewTone: Record<GadsSearchSection['review']['status'], Tone> = {
  not_required: 'neutral',
  pending: 'amber',
  approved: 'moss',
  rejected: 'rose',
};

/** Google'ın değeri (APPROVED, GOOD…) → metin; bilinmeyen değer olduğu gibi. */
function useGoogleLabel() {
  const t = useT();
  return (group: string, v: string | null) => {
    if (!v) return '—';
    const s = t(`${G}.${group}.${v}`);
    return s === `${G}.${group}.${v}` ? v : s;
  };
}

function KeywordList({ items, empty }: { items: GadsSearchSection['keywords']; empty: string }) {
  const t = useT();
  if (!items.length) return <p className="text-sm text-ink-3">{empty}</p>;
  return (
    <ul className="flex flex-wrap gap-2">
      {items.map((k) => (
        <li
          key={`${k.matchType}:${k.text}`}
          className="inline-flex items-center gap-1 rounded-full border border-line px-2.5 py-0.5 text-sm"
        >
          <span>
            {k.matchType === 'exact' ? `[${k.text}]` : k.matchType === 'phrase' ? `"${k.text}"` : k.text}
          </span>
          <span className="text-xs text-ink-3">{t(`${G}.matchTypes.${k.matchType}`)}</span>
          {!k.applied && <Badge tone="neutral">{t(`${G}.notSent`)}</Badge>}
        </li>
      ))}
    </ul>
  );
}

function TextReview({ campaignId, search: s }: { campaignId: string; search: GadsSearchSection }) {
  const t = useT();
  const { can } = usePermissions();
  const [note, setNote] = useState('');
  const decide = useApiMutation(
    (decision: 'approved' | 'rejected') =>
      api.post(`/v1/admin/google-ads/campaigns/${campaignId}/text-review`, {
        decision,
        contentHash: s.requestedHash,
        note: note.trim() || undefined,
        version: s.version,
      }),
    {
      invalidate: [['gads-campaign', campaignId], ['gads-approvals']],
      success: t(`${G}.reviewSaved`),
    },
  );
  if (!s.review.required) return null;
  return (
    <div className="space-y-2 rounded-md border border-line p-3" aria-label={t(`${G}.reviewTitle`)}>
      <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
        {t(`${G}.reviewTitle`)}{' '}
        <Badge tone={reviewTone[s.review.status]}>{t(`${G}.reviewStatuses.${s.review.status}`)}</Badge>
        <HelpTip k="gads.text_review" />
      </p>
      <Findings findings={s.findings} />
      {s.review.reviewedByName && (
        <p className="text-sm text-ink-2">
          {t(`${G}.reviewedBy`, { name: s.review.reviewedByName })}
          {s.review.note ? ` — ${s.review.note}` : ''}
        </p>
      )}
      {s.review.status === 'pending' && can('content.expert_review') && (
        <div className="space-y-2">
          <Field label={t(`${G}.reviewNote`)} description={t(`${G}.reviewNoteHint`)}>
            {(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              loading={decide.isPending && decide.variables === 'approved'}
              onClick={() => decide.mutate('approved')}
            >
              {t(`${G}.reviewApprove`)}
            </Button>
            <Button
              size="sm"
              disabled={!note.trim()}
              loading={decide.isPending && decide.variables === 'rejected'}
              onClick={() => decide.mutate('rejected')}
            >
              {t(`${G}.reviewReject`)}
            </Button>
            <HelpTip k="gads.text_review_decide" />
          </div>
        </div>
      )}
      {s.review.status === 'pending' && !can('content.expert_review') && (
        <p className="text-sm text-ink-2">{t(`${G}.reviewWaiting`)}</p>
      )}
    </div>
  );
}

/** Arama kampanyasının reklam metni, uzman incelemesi, Google'daki durum ve anahtar kelimeler. */
export function SearchSection({
  campaign: c,
  search: s,
  manage,
  busy,
}: {
  campaign: GadsCampaign;
  search: GadsSearchSection;
  manage: boolean;
  busy: boolean;
}) {
  const t = useT();
  const label = useGoogleLabel();
  const [editing, setEditing] = useState<'ad' | 'keywords' | 'negatives' | null>(null);
  const shown = s.ad ?? s.requestedAd;
  const edit = (
    k: 'ad' | 'keywords' | 'negatives',
    help: 'gads.search_edit_ad' | 'gads.search_edit_keywords' | 'gads.search_edit_negatives',
  ) => (
    <div className="flex items-center gap-1">
      {manage && (
        <Button size="sm" disabled={busy} onClick={() => setEditing(k)}>
          {t(`${G}.edit.${k}`)}
        </Button>
      )}
      <HelpTip k={help} />
    </div>
  );
  return (
    <>
      <Section
        title={t(`${G}.adTitle`)}
        description={t(`${G}.adLead`)}
        action={edit('ad', 'gads.search_edit_ad')}
      >
        <div className="space-y-3">
          <SearchAdPreview ad={shown} />
          {s.ad && !s.applied && <p className="text-sm text-amber">{t(`${G}.requestedDiffers`)}</p>}
          {s.google && (
            <dl className="grid gap-2 text-sm sm:grid-cols-3" aria-label={t(`${G}.googleState`)}>
              <div className="rounded-md border border-line px-3 py-2">
                <dt className="flex items-center gap-1 text-xs text-ink-3">
                  {t(`${G}.approvalStatus`)} <HelpTip k="gads.search_google_state" />
                </dt>
                <dd>
                  <Badge tone={approvalTone[s.google.approvalStatus ?? ''] ?? 'neutral'}>
                    {label('approvalStatuses', s.google.approvalStatus)}
                  </Badge>
                  {s.google.policyTopics.length > 0 && (
                    <span className="ms-1 text-ink-2">({s.google.policyTopics.join(', ')})</span>
                  )}
                </dd>
              </div>
              <div className="rounded-md border border-line px-3 py-2">
                <dt className="text-xs text-ink-3">{t(`${G}.googleReview`)}</dt>
                <dd>{label('googleReviewStatuses', s.google.reviewStatus)}</dd>
              </div>
              <div className="rounded-md border border-line px-3 py-2">
                <dt className="text-xs text-ink-3">{t(`${G}.adStrength`)}</dt>
                <dd>{label('adStrengths', s.google.adStrength)}</dd>
              </div>
            </dl>
          )}
          <TextReview campaignId={c.id} search={s} />
        </div>
      </Section>
      <Section
        title={t(`${G}.keywords`)}
        description={t(`${G}.keywordsLead`)}
        action={edit('keywords', 'gads.search_edit_keywords')}
      >
        <KeywordList items={s.keywords} empty={t(`${G}.noKeywords`)} />
      </Section>
      <Section
        title={t(`${G}.negatives`)}
        description={t(`${G}.negativesLead`)}
        action={edit('negatives', 'gads.search_edit_negatives')}
      >
        <KeywordList items={s.negatives} empty={t(`${G}.noNegatives`)} />
      </Section>
      {editing === 'ad' && <AdTextDialog campaign={c} search={s} onClose={() => setEditing(null)} />}
      {(editing === 'keywords' || editing === 'negatives') && (
        <KeywordsDialog campaign={c} search={s} kind={editing} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

/** Taslakta metin doğrudan kaydedilir; Google'da oluşmuş kampanyada değişiklik onaylı istektir. */
function AdTextDialog({
  campaign: c,
  search: s,
  onClose,
}: {
  campaign: GadsCampaign;
  search: GadsSearchSection;
  onClose: () => void;
}) {
  const t = useT();
  const [ad, setAd] = useState<SearchAdValue>(() => padAd(s.ad ?? s.requestedAd));
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const draft = !c.created;
  const save = useApiMutation(
    () =>
      draft
        ? api.patch(`/v1/admin/google-ads/campaigns/${c.id}`, {
            search: { ad: cleanAd(ad) },
            version: c.version,
          })
        : api.post(
            `/v1/admin/google-ads/campaigns/${c.id}/operations`,
            { kind: 'ad_text', ad: cleanAd(ad), note: note.trim() || undefined },
            { idempotencyKey: idem },
          ),
    {
      invalidate: [['gads-campaign', c.id], ['gads'], ['gads-approvals']],
      success: draft ? t(`${G}.draftSaved`) : t('admin.gads.sentForApproval'),
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t(`${G}.edit.ad`)}
      description={draft ? t(`${G}.draftEditLead`) : t(`${G}.adChangeLead`)}
      closeLabel={t('common.close')}
      variant="sheet"
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={adProblems(ad).length > 0}
            onClick={() => save.mutate(undefined)}
          >
            {draft ? t('common.save') : t('admin.gads.sendForApproval')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <SearchAdEditor
          value={ad}
          onChange={(v) => setAd(v)}
          storeId={c.storeId}
          findings={[]}
          errors={save.error}
        />
        {!draft && (
          <Field label={t('admin.gads.requestNote')} help={<HelpTip k="gads.request_note" />}>
            {(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
        )}
      </div>
    </Dialog>
  );
}

/**
 * Anahtar kelime / negatif listesi. Onay matrisi sunucuyla aynı: anahtar kelime eklemek ve negatif çıkarmak onaylı
 * (erişim genişler); yalnızca anahtar kelime çıkarmak veya negatif eklemek onaysız ("uygula" onay kutusu).
 */
function KeywordsDialog({
  campaign: c,
  search: s,
  kind,
  onClose,
}: {
  campaign: GadsCampaign;
  search: GadsSearchSection;
  kind: 'keywords' | 'negatives';
  onClose: () => void;
}) {
  const t = useT();
  const current = s[kind].map(({ text, matchType }) => ({ text, matchType }));
  const [list, setList] = useState<KeywordValue[]>(current);
  const [note, setNote] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [idem] = useState(() => new IdempotencyKey());
  const draft = !c.created;
  const clean = list
    .filter((k) => k.text.trim())
    .map((k) => ({ ...k, text: k.text.replace(/\s+/g, ' ').trim() }));
  const have = new Set(current.map(keyOf));
  const want = new Set(clean.map(keyOf));
  const added = clean.filter((k) => !have.has(keyOf(k))).length;
  const removed = current.filter((k) => !want.has(keyOf(k))).length;
  const needsApproval = kind === 'keywords' ? added > 0 : removed > 0;
  const save = useApiMutation(
    () =>
      draft
        ? api.patch(`/v1/admin/google-ads/campaigns/${c.id}`, {
            search: { [kind]: clean },
            version: c.version,
          })
        : api.post(
            `/v1/admin/google-ads/campaigns/${c.id}/operations`,
            {
              kind,
              [kind]: clean,
              note: note.trim() || undefined,
              ...(needsApproval ? {} : { confirm }),
            },
            { idempotencyKey: idem },
          ),
    {
      invalidate: [['gads-campaign', c.id], ['gads'], ['gads-approvals']],
      success: draft
        ? t(`${G}.draftSaved`)
        : needsApproval
          ? t('admin.gads.sentForApproval')
          : t('admin.gads.queued'),
      onSuccess: onClose,
    },
  );
  const unchanged = added === 0 && removed === 0;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t(`${G}.edit.${kind}`)}
      description={
        draft
          ? t(`${G}.draftEditLead`)
          : needsApproval
            ? t('admin.gads.approvalLead')
            : t('admin.gads.noApprovalLead')
      }
      closeLabel={t('common.close')}
      variant="sheet"
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={
              unchanged || (kind === 'keywords' && !clean.length) || (!draft && !needsApproval && !confirm)
            }
            onClick={() => save.mutate(undefined)}
          >
            {draft
              ? t('common.save')
              : needsApproval
                ? t('admin.gads.sendForApproval')
                : t('admin.gads.applyNow')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <KeywordsEditor
          label={t(`${G}.${kind}`)}
          lead={t(`${G}.${kind}Lead`)}
          help={kind === 'keywords' ? 'gads.search_keywords' : 'gads.search_negatives'}
          value={list}
          onChange={setList}
          max={200}
        />
        {kind === 'keywords' && (
          <KeywordIdeas
            storeId={c.storeId}
            seeds={clean.map((k) => k.text)}
            url={(s.ad ?? s.requestedAd).finalUrl}
            onAdd={(texts) =>
              setList((cur) => [
                ...cur,
                ...texts
                  .map((text) => ({ text, matchType: 'phrase' as const }))
                  .filter((k) => !cur.some((x) => keyOf(x) === keyOf(k))),
              ])
            }
          />
        )}
        <p className="text-sm text-ink-2" aria-live="polite">
          {t(`${G}.changeSummary`, { added, removed })}
        </p>
        {!draft && (
          <Field label={t('admin.gads.requestNote')} help={<HelpTip k="gads.request_note" />}>
            {(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
        )}
        {!draft && !needsApproval && !unchanged && (
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 accent-[var(--zt-pine)]"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
            />
            <span>{t('admin.gads.confirmApply')}</span>
          </label>
        )}
      </div>
    </Dialog>
  );
}
